import {Component, Input, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ox/translate';
import {CtaType, MyPlan, ServiceUsageType, ServiceUsage, pageTypes, Vehicle} from '@ox/typings';
import {AppConfigService, AutoDestroy,buildTrackingString} from '@ox/utils';
import {CopyFormattingService, ShopDataService} from '@webu/data';
import {selectActiveVehicle} from '@ox/data';
import {Subject, catchError, filter, map, of, switchMap, takeUntil, throwError} from 'rxjs';

@Component({
  selector: 'webu-data-bar',
  templateUrl: './data-bar.component.html'
})
export class DataBarComponent implements OnInit{
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Input() plan: MyPlan;
  vin: string;
  dataLoaded = false;
  serviceUsage: ServiceUsage;
  dataRemainingDescription: string;
  addDataLabel: string;
  provider: string | undefined;
  showServiceProvider = this.appConfig.appSettings.moduleConfig.showServiceProvider || false;
  showDataBar = false;
  isTelus = false;
  activeVehicle$ = this.store.select(selectActiveVehicle)
    .pipe(
      takeUntil(this.destroy$),
      filter((vehicle): vehicle is Vehicle => !!vehicle),
      catchError(err => throwError(() => new Error(`Active Vehicle Retrieval Error: ${err.message}`)))
    );

  getServiceUsage$ = (vin : string, externalOrderNumber: string) => this.shopDataService.getServiceUsage(vin, externalOrderNumber).pipe(
    takeUntil(this.destroy$), 
    map((response: ServiceUsageType) => {
      if (!response) return undefined;
      
      this.serviceUsage = response.serviceUsages[0];
      this.setText();
    }),
    catchError(err => throwError(() => new Error(`Service Usage API Error: ${err.message}`)))
  );

  //Tracking imports
  trackingString = (cta: CtaType, args: string[]) => buildTrackingString(pageTypes.myPlans, cta, args);
  
  constructor (
    private shopDataService: ShopDataService,
    private store: Store,
    private router: Router,
    private translate: TranslateService,
    private appConfig: AppConfigService,
    private copyService: CopyFormattingService
  ){}

  ngOnInit(): void {
    if (!this.plan.hasDataBar && this.plan.isData) this.isTelus = true;
    if (this.plan.hasDataBar) this.showDataBar = true;
    else {
      this.plan.features?.forEach((feature) => {
        if(feature.isData === true) {
          feature.codes.forEach((code) => {
            if(code.hasDataBar && this.appConfig.appSettings.moduleConfig.showBundledPlanDataBar) {
              this.showDataBar = true;
            }
          });
        }  
      });
    }

    this.activeVehicle$.pipe(
      switchMap(vehicle => {
        if (vehicle.vin && this.plan.externalOrderNumber && !this.isTelus) {
          return this.getServiceUsage$(vehicle.vin, this.plan.externalOrderNumber);
        } else {
          return of(undefined);
        }
      })
    ).subscribe({
      next: () => {
        this.dataLoaded = true;
      },
      error: (err) => console.error(err)
    });
  }

  setText(): void {
    this.dataRemainingDescription =this.shopDataService.getGenericCopy('myplans.dataremaining', {remaining: String(this.serviceUsage?.quantityRemaining), total: String(this.serviceUsage?.totalQuantity)}) ?? '';
  }

  isUnlimitedData(): boolean {
    return this.serviceUsage?.totalQuantity >= 976;
  }

  goToDataPdp() {
    this.translate.get('data_seo_url')
      .pipe(takeUntil(this.destroy$))
      .subscribe(url => this.router.navigate([url]));
  }

  getGenericCopy$ = (code: string, varMap?: Record<string, string>) => 
    this.copyService.getGenericCopy$(code, varMap);

}
